import React, { useState, useContext } from 'react';
import { Form, Input, Button, message } from 'antd';
import { Lock, Mail, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import apiClient from '../../lib/apiClient';
import { LOGIN } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';
import { getFirstPermittedRoute } from '../../routes/ProtectedRoute';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await apiClient.post(LOGIN, {
        login_name: values.username,
        login_password: values.password,
      });

      if (response?.token) {
        login(response);
        ToastMsg('success', 'Login successful');
        const perms = response.permissions || {};
        const checkPerm = (mod, action = 'view') => {
          if (response.is_admin) return true;
          const key = action.startsWith('can_') ? action : `can_${action}`;
          return perms?.[mod]?.[key] ?? false;
        };
        navigate(getFirstPermittedRoute(checkPerm));
      } else if (response?.status === 'success') {
        const loginData = response.data || response;
        login(loginData);
        ToastMsg('success', 'Login successful');
        const perms = loginData.permissions || {};
        const checkPerm = (mod, action = 'view') => {
          if (loginData.is_admin) return true;
          const key = action.startsWith('can_') ? action : `can_${action}`;
          return perms?.[mod]?.[key] ?? false;
        };
        navigate(getFirstPermittedRoute(checkPerm));
      } else {
        ToastMsg('error', response?.message || 'Invalid credentials');
      }
    } catch (error) {
      ToastMsg(
        'error',
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 to-purple-700 px-4">
      <div className="w-full max-w-md">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-card rounded-2xl shadow-lg mb-4">
            <Building2 className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-white">ERP Platform</h1>
          <p className="text-indigo-200 mt-2">Sign in to your account</p>
        </div>

        {/* Login card */}
        <div className="bg-card rounded-2xl shadow-2xl p-8">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="username"
              label="Email or Username"
              rules={[
                { required: true, message: 'Please enter your email or username' },
              ]}
            >
              <Input
                prefix={<Mail className="w-4 h-4 text-muted-foreground" />}
                placeholder="you@company.com"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter your password' },
              ]}
            >
              <Input.Password
                prefix={<Lock className="w-4 h-4 text-muted-foreground" />}
                placeholder="Enter your password"
              />
            </Form.Item>

            <Form.Item className="mb-0 mt-6">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="h-12 text-base font-semibold bg-indigo-600 hover:bg-indigo-700 border-none rounded-lg"
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </div>

        <p className="text-center text-indigo-200 text-sm mt-6">
          &copy; {new Date().getFullYear()} ERP Platform. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
